import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from '@angular/router';
import { AuthorizationService, ConfirmationDialogService, NotificationDialogService, NotificationPopupService } from '@myapp/modules/fwcore/services';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { Keys } from '@progress/kendo-angular-common';
import { AddEvent,  CancelEvent, CellClickEvent, CellCloseEvent, GridModule, RemoveEvent, SaveEvent } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { Subject, forkJoin } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ControlResizerOptions, ControlResizerService } from "../../../../../../services/framework";
import { UserImport, ModuleChecked, UserState, RequestModule, UserStateChecked, IUserLicenseEditor, RequestModuleUserSet } from '../../../models';
import { RequestUserImportSet } from '../../../models/messages';
import { AdminService } from '../../../services';
import { BaseGridComponent } from '../../base-grid/base-grid.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { ModuleUserAddComponent } from '../../../components/modules/module-user-add/module-user-add.component'
import { ModuleUserAddForm } from '../../../components/modules/module-user-add/module-user-add.form'
import { ComboBoxModule, MultiSelectModule, DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { FormsModule } from '@angular/forms';
import { SharedUiListviewComponent } from '../../../../../modules/forms/components/shared-ui-listview/shared-ui-listview.component';
import { NgFor } from '@angular/common';

@Component({
  selector: 'app-user-import',
  templateUrl: './user-import.component.html',
  styleUrls: ['./user-import.component.scss'],
  standalone: true,
  imports: [GridModule, ButtonModule, MultiSelectModule, SharedUiListviewComponent, FormsModule, DropDownListModule, NgFor]
})

export class UserImportComponent extends BaseGridComponent<UserState> implements OnInit, OnDestroy {

  /*-------------------------- Input / Outputs -------------------------*/

  @Input() public companyId: string | null = null;
  @Input() public licenseId: string | null = null;



  /*-------------------------- public properties / methods -------------*/

  public gridState: State = {
    sort: [],
    skip: 0,
    take: 20
  };

  public heightChange(options?: ControlResizerOptions): void {

    let ctrlOptions: ControlResizerOptions = new ControlResizerOptions();
    ctrlOptions.elemTop = this.elem.nativeElement.getBoundingClientRect().top;

    if (options) {
      ctrlOptions = { ...options, elemTop: ctrlOptions.elemTop };
    }

    const h = this.controlResizer.calculateHeight(ctrlOptions);
    //this.heightGrid = h;

  }

  /*-------------------------- Internal properties  --------------------*/

  protected dataItems: UserImport[] = [];

  protected heightGrid!: number;

  protected destroy$ = new Subject();

  protected viewInit: boolean = false;

  protected loading: boolean = true;

  protected modules: ModuleChecked[] = [];

  protected licenseModules: RequestModule = {}

  protected userData: UserStateChecked[] = [];

  protected userModules: RequestModuleUserSet = {

  }

  protected isNew?: boolean;

  public checkboxResult: any = [];


  /*--------------------------- Constructor  --------------------------*/

  constructor( adminService: AdminService
    , protected serviceAuth: AuthorizationService
    , protected controlResizer: ControlResizerService
    , protected router: Router
    , protected formBuilder: FormBuilder
    , protected notification: NotificationPopupService
    , protected dialogConfirm: ConfirmationDialogService
    , protected dialogNotification: NotificationDialogService
    , protected elem: ElementRef
    , modalService: NgbModal) {
    super(adminService, modalService);
  }

  /*--------------------------- ng  -----------------------------------*/

  ngOnInit(): void {

    this.viewInit = true;
    this.sizeChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /*--------------------------- event handlers  -----------------------*/

  @HostListener('window:resize', ['$event'])
  protected onResize(event: Event) {
    this.sizeChange();
  }

  @HostListener('paste', ['$event'])
  public onPaste(ev: ClipboardEvent) {

    if (ev.target && (ev.target as HTMLElement).tagName && (ev.target as HTMLElement).tagName.match(/(input|textarea)/i)) {

      // Do not handle past when an input element is currently focused
      return;
    }

    ev.preventDefault();

    // Get clipboard data as text
    const data = ev.clipboardData?.getData('text');
    if (data === undefined || data.length < 1)
      return;

    // Simplified parsing of the TSV data with hard-coded columns
    const rows = data.split('\n');

    const result: any[] = rows.map((row: string |any) => {
      const cells = row.split('\t');
      const r: UserImport = {
        email: cells[0],
        firstName: cells[1],
        alias: cells[2],
        lastName: cells[3],
        modules: cells[4]
      };
      return r;
    });

    this.onExcelPaste(result);
  }

  public onExcelPaste(data: UserImport[] | null): void {
    this.dataItems = data ?? [];
  }

  public onAddRow(args: AddEvent): void {
    args.sender.addRow(this.createFormGroup(new UserImport()));
    this.licenseModules.licenseId = this.licenseId;
    forkJoin([
      /*this.adminService.getAllLicenseModules(this.licenseModules)*/
      this.adminService.getAllLicenseModules(this.licenseId)
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([licenseModules]) => {
        this.modules = licenseModules.data.filter((x: any) => x.licenseModuleID != null);
        this.checkboxResult = licenseModules.data.filter((x: any) => x.licenseModuleID != null).map((i: any) => i.id);
      });
  }

  public onSaveHandler(args: SaveEvent): void {
    if (args.formGroup.valid) {
      args.dataItem.modules = this.modules.filter((x: any) => this.checkboxResult.includes(x.id)).map((i:any)=>i.name);
      if (args.isNew)
        this.dataItems.unshift(args.dataItem);
      else {

      }

      args.sender.closeRow(args.rowIndex);

    }
  }

  public onRemoveHandler(args: RemoveEvent): void {

    const index = this.dataItems.indexOf(args.dataItem, 0);
    if (index > -1) {
      this.dataItems.splice(index, 1);
    }


    args.sender.cancelCell();
  }

  public onCancelHandler(args: CancelEvent): void {
    args.sender.closeRow(args.rowIndex);
  }

  public onCellClickHandler(args: CellClickEvent) {

    if (!args.isEdited) {
      args.sender.editCell(
        args.rowIndex,
        args.columnIndex,
        this.createFormGroup(args.dataItem)
      );
    }
  }

  public onCellCloseHandler(args: CellCloseEvent): void {
    const { formGroup, dataItem } = args;

    if (!formGroup.valid) {
      // prevent closing the edited cell if there are invalid values.
      args.preventDefault();
    } else if (formGroup.dirty) {
      if (args.originalEvent && args.originalEvent.keyCode === Keys.Escape) {
        return;
      }

      const index = this.dataItems.indexOf(dataItem, 0);
      if (index > -1) {
        this.dataItems.splice(index, 1, formGroup.value);
      }
    }
  }

  public onStateChange(state: State): void {

    this.gridState = state;

  }

  protected onUsersClearAll() {

    this.dialogConfirm.open('Confirm Clear', 'Are you sure you want to clear all the users from the grid?')
      .subscribe((result: any) => {
        if (result.value) {
          this.gridUsersClear();
        }
      });
  }

  public addHandler({ sender }: any)
  {
    let test = '';

  }

  public editHandler({ sender }: any) {

  }

  public removeHandler({ dataItem }: any): void {

  }

  retrieveItems(): void {
  }


  refreshItems(): void {
  }

  protected onAddUser({ sender, rowIndex, dataItem }: any) {
    this.licenseModules.id = this.licenseId;
    const size = 'lg';
    const options = {
      title: "Add User", windowClass: "modal-preferences",
      showSave: true, showCancel: true, showCross: true, scrollable: false
    };

    this.licenseModules.licenseId = this.licenseId;
    forkJoin([
      //this.adminService.getAllLicenseModules(this.licenseModules)
      this.adminService.getAllLicenseModules(this.licenseId)
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([licenseModules]) => {
        this.modules = licenseModules.data;
        dataItem = this.modules.filter(x => x.licenseModuleID != null);
        //this.checkboxResult = this.modules.filter(x => x.licenseModuleID != null).map((i: any) => i.id);;
        this.showEditorModal(size, options, ModuleUserAddComponent, new ModuleUserAddForm(dataItem), false);
      });
  }

  saveItem(item: IUserLicenseEditor, isNew: boolean): void {
    let data: UserImport = {
      modules:item.checkedIds,
      firstName :item.firstName,
      lastName : item.lastName,
      email : item.email
    }

    this.dataItems.push(data);
   
    const req: RequestUserImportSet = {
      licenseId: this.licenseId
      , companyId: this.companyId
      , data: this.dataItems
    };

    this.userImport(req);

  }

  protected onImportUsers() {

    this.dialogConfirm.open('Confirm upload', 'Are you sure you want to proceed and create the new users?')
      .subscribe((result: any) => {
        if (result.value) {
          this.dataItems[0].modules = this.modules.filter((x: any) => this.checkboxResult.includes(x.id)).map((i: any) => i.id);
          const req: RequestUserImportSet = {
            licenseId: this.licenseId
            , companyId: this.companyId
            , data: this.dataItems
          };

          this.userImport(req);
        }
      });
  }

  /*--------------------------- internal functions  -------------------*/

  protected sizeChange() {

    if (this.viewInit) {
      var options: ControlResizerOptions = new ControlResizerOptions();

      options.elemTop = 240;
      this.heightGrid = this.controlResizer.calculateHeight(options);
    }
  }

  protected createFormGroup(dataItem: UserImport): FormGroup {

    return this.formBuilder.group({
      email: [dataItem.email, Validators.required],
      firstName: [dataItem.firstName, Validators.required],
      lastName: dataItem.lastName,
      alias: dataItem.alias,
      modules: dataItem.modules
    });
  }

  protected gridUsersClear() {
    this.dataItems = [];
  }

  protected userImport(req: RequestUserImportSet) {

    this.adminService.setUserImport$(req)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe((res) => {
        if (res.success && res.data && res.data?.length > 0) {

          this.dataItems = res.data;

          const newUsers = res.data.filter(item => item.exists === false).length;
          const existingUsers = res.data.filter(item => item.exists === true).length;

          let msg = `${newUsers} user(s) were added`;
          if (existingUsers > 0)
            msg += ` and ${existingUsers} user(s) already existed`;

          this.dialogNotification.openNotificationDialog("Import Users", msg);

        }
      });
  }

  onCheckChange(event: Event) {
    const inputTarget = event.target as HTMLInputElement;
    
    const ischecked = (<HTMLInputElement>event.target).checked;

    if (ischecked) {
      if (this.checkboxResult.filter((x: any) => x.id != inputTarget.value)) {
        let val = this.modules.filter((x: any) => x.id == inputTarget.value).map((i: any) => i.id);
        this.checkboxResult = this.checkboxResult.concat(val);
      }
    }
    else {
      let val = this.checkboxResult.filter((x: any) => x != inputTarget.value);
      this.checkboxResult = val;
    }

  }

}

